/* eslint-disable @typescript-eslint/no-use-before-define */
import { useState } from 'react';
import * as React from 'react';
import { Portal } from 'react-portal';
import { AnimatePresence, motion } from 'framer-motion';
import { rgba } from 'polished';
import styled, { css, useTheme } from 'styled-components';

import useMediaQuery from '@/utils/useMediaQuery';

import { CloseIcon } from '../../icons/Icons';
import { ReactComponent as IconCustomers } from '../../images/icons/icon-customers.svg';
import { ReactComponent as IconOpenArrow } from '../../images/icons/icon-open-arrow.svg';
import { ReactComponent as IconTemplates } from '../../images/icons/icon-templates.svg';
import { Button, CornerLines, Heading } from '../design-system';
import { HexEmbed } from '../global/HexEmbed';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 80px 40px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    flex-direction: column;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    padding: 60px 30px;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: none;
  width: 100%;
`;

const EmbedWrapper = styled.div`
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  width: 100%;
  height: 80vh;
  margin: 0 auto;
  position: relative;
`;

const Embeds = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: white;
`;

const EmbedOption = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  ${({ $visible }) =>
    $visible
      ? css`
          display: block;
        `
      : css`
          display: none;
        `}
`;

const FullScreenEmbed = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => rgba(theme.colors.BLACK, 0.8)};
  z-index: 99999999999;
  padding: 20px;

  video,
  iframe {
    width: 95%;
    height: 95%;
    max-width: 100%;
    max-height: 100%;
    outline: none;
    z-index: 1;
    position: relative;
  }

  .bkg {
    position: absolute;
    z-index: 0;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

const FullScreenButton = styled.div`
  position: absolute;
  z-index: 999999999;
  background-color: ${({ theme }) => theme.colors.BLACK};
  bottom: 10px;
  right: 10px;
`;

const CloseButton = styled(Button)`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    position: absolute;
    z-index: 2;
    top: 10px;
    right: 10px;
    background-color: ${({ theme }) => theme.colors.BLACK};
    border-left: 1px solid ${({ theme }) => theme.highlightColor};
    border-bottom: 1px solid ${({ theme }) => theme.highlightColor};
    cursor: pointer;
  }
`;

const Tabs = styled.div`
  display: flex;
  border-top: 1px solid ${({ theme }) => rgba(theme.highlightColor, 0.35)};
  flex: none;

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    width: 100%;
  }
`;

const TabOption = styled(motion.div)<{ $selected: boolean }>`
  padding: 15px 0 30px;
  opacity: 0.6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  width: 240px;
  flex: none;
  gap: 8px;
  user-select: none;
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING},
    opacity ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    width: auto;
    flex: 1 1 auto;
    padding: 15px 0 20px;
  }

  &:hover {
    opacity: 1;
  }

  &:active {
    background: linear-gradient(
      180deg,
      rgba(245, 192, 192, 0.05) 0%,
      rgba(245, 192, 192, 0) 190%
    );
  }

  ${({ $selected }) =>
    $selected &&
    css`
      opacity: 1;
      background: linear-gradient(
        180deg,
        rgba(245, 192, 192, 0.098) 0%,
        rgba(245, 192, 192, 0) 100%
      );

      @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
        background: rgba(245, 192, 192, 0.098);
      }
    `}
`;

interface LiveDemoProps {
  logicLink: string;
  publishedAppLink: string;
}

type EmbedType = 'published' | 'logic';

export const LiveDemo: React.ComponentType<LiveDemoProps> = React.memo(
  ({ logicLink, publishedAppLink }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.mediaQueryString.SMALL);
    const [embedSelected, setEmbedSelected] = useState<EmbedType>('published');
    const [fullScreenEmbed, setFullScreenEmbed] = useState(false);

    return (
      <>
        <Wrapper>
          <Info>
            <Heading
              css={`
                text-align: center;
                margin-bottom: 30px;
              `}
              renderAs="h2">
              Explore a live demo
            </Heading>
            {!isMobile && (
              <Tabs>
                <TabOption
                  $selected={embedSelected === 'published'}
                  onClick={() => setEmbedSelected('published')}>
                  <IconTemplates />
                  What you can build (app)
                </TabOption>
                <TabOption
                  $selected={embedSelected === 'logic'}
                  onClick={() => setEmbedSelected('logic')}>
                  <IconCustomers />
                  How it's built (logic)
                </TabOption>
              </Tabs>
            )}
          </Info>
          <EmbedWrapper>
            <CornerLines
              bottomLeft={true}
              bottomRight={true}
              topLeft={true}
              topRight={true}
            />
            <Embeds>
              <EmbedOption $visible={embedSelected === 'published'}>
                <HexEmbed
                  height="100%"
                  src={publishedAppLink + '?embedded=true'}
                />
              </EmbedOption>
              <EmbedOption $visible={embedSelected === 'logic'}>
                <HexEmbed height="100%" src={logicLink + '?embedded=true'} />
              </EmbedOption>
            </Embeds>
            <FullScreenButton>
              <Button
                icon={<IconOpenArrow />}
                size="large"
                text="Full screen"
                onClick={() => {
                  setFullScreenEmbed(true);
                }}
              />
            </FullScreenButton>
          </EmbedWrapper>
        </Wrapper>
        <AnimatePresence>
          {fullScreenEmbed && (
            <Portal>
              <FullScreenEmbed>
                <CloseButton
                  icon={<CloseIcon />}
                  size="small"
                  stylez="bold"
                  onClick={() => {
                    setFullScreenEmbed(false);
                  }}
                />
                <HexEmbed
                  height="90vh"
                  src={
                    embedSelected === 'published' ? publishedAppLink : logicLink
                  }
                />
                <div
                  className="bkg"
                  onClick={() => {
                    setFullScreenEmbed(false);
                  }}
                />
              </FullScreenEmbed>
            </Portal>
          )}
        </AnimatePresence>
      </>
    );
  }
);
